import type { PersistStorage, StorageValue } from 'zustand/middleware';
import { SuperSaveSystem } from '@game-libs/super-save-system';

/**
 * Creates a Zustand-compatible persist storage adapter backed by SuperSaveSystem.
 *
 * SuperSaveSystem handles the actual backend (localStorage, Gemshell, etc.)
 * so swapping backends requires no changes here.
 */
export function createPersistAdapter<T>(): PersistStorage<T> {
  const { save, load } = SuperSaveSystem();

  return {
    getItem: async (name: string): Promise<StorageValue<T> | null> => {
      const data = await load(name, null);
      if (data === null || data === undefined) return null;
      // Backends may still return a JSON string; normalize before handing Zustand the state object.
      if (typeof data === 'string') {
        try {
          return { state: JSON.parse(data) as T };
        } catch {
          return null;
        }
      }
      return { state: data as T };
    },

    setItem: async (name: string, value: StorageValue<T>): Promise<void> => {
      // Zustand hands persist the full store (actions + state). localStorage worked because
      // JSON.stringify drops functions; GemShell uses clone-based IPC and throws
      // "An object could not be cloned" if we pass functions through.
      const storable = JSON.parse(JSON.stringify(value.state)) as T;
      await save(name, storable);
    },

    removeItem: async (name: string): Promise<void> => {
      // SuperSaveSystem.clear() wipes everything; targeted removal is a no-op
      // unless your backend supports it. Override this if needed.
      await save(name, null);
    },
  };
}
